import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { ChangeDetectorRef, ElementRef, Renderer2 } from '@angular/core';
import { DialogService } from 'src/app/services/dialog.service';
import { EntityRESTService } from 'src/app/services/entity-rest.service';
import { UtilityService } from 'src/app/services/utility.service';
import { FieldsService } from 'src/app/services/fields.service';
import { DrupalRESTService } from 'src/app/services/drupal-rest.service';
import { TaxonomyService } from 'src/app/services/taxonomy.service';
import { AuthRESTService } from 'src/app/services/auth-rest.service';
import { AuthService } from 'src/app/services/auth.service';
import { MenuRESTService } from 'src/app/services/menu-rest.service';
import { FormBuilder } from '@angular/forms';
import { RouteEventsService } from 'src/app/route-events.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { ConfigPagesService } from 'src/app/services/config-pages.service';
import { PackagesService } from 'src/app/services/packages.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UserService } from 'src/app/services/user.service';
import { DomSanitizer } from '@angular/platform-browser';
import { RefreshService } from 'src/app/services/refresh.service';
import { BaseComponent } from '../forms/base-form/base-form.component';
import { StudentInquiryEntityComponent } from '../forms/student-inquiry-entity/student-inquiry-entity.component';
import { StudentAccountEntityComponent } from '../forms/student-account-entity/student-account-entity.component';
import { SupportRequestComponent } from '../forms/support-request/support-request.component';
import { PaymentsEntityComponent } from '../forms/payments-entity/payments-entity.component';
import { EnrollmentEntityComponent } from '../forms/enrollment-entity/enrollment-entity.component';
import { PackageEntityComponent } from '../forms/package-entity/package-entity.component';
import { ComponentType } from '@angular/cdk/overlay';
import { Subscription } from 'rxjs';
import { LoggerService } from '../../services/logger.service';

@Component({
  selector: 'app-menu',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.css']
})
export class MenuComponent extends BaseComponent implements OnInit, OnDestroy {

  // logo: string = "assets/images/logo.png";
  logo: string = "assets/images/logo-white.png";
  studios: any[] = [];
  selectedStudio: string;
  navbarOpen = false;
  studioSearchText: string = '';
  filteredStudios: any[] = [];
  private studiosSubscription: Subscription;
  isMac: boolean = navigator.platform.toUpperCase().indexOf('MAC') >= 0;

  StudentInquiryEntityComponent = StudentInquiryEntityComponent;
  StudentAccountEntityComponent = StudentAccountEntityComponent;
  supportRequestForm = SupportRequestComponent;
  PaymentsEntityComponent = PaymentsEntityComponent;
  EnrollmentEntityComponent = EnrollmentEntityComponent;
  PackageEntityComponent = PackageEntityComponent;

  menuItems = [
    { title: 'Dashboard', path: '/dashboard', icon: 'dashboard' },
    { title: 'Students', path: '/students', icon: 'people' },
    { title: 'Enrollments', path: '/enrollment', icon: 'school' },
    { title: 'Payments', path: '/payment-reports', icon: 'payment' },
    { title: 'Packages', path: '/packages-list', icon: 'inventory_2' },
    { title: 'Calendar', path: '/calendar', icon: 'calendar_today' },
    { title: 'Reports', path: '/reports', icon: 'assessment' },
  ];

  /**
   * Utility to open dialogs with a default configuration.
   *
   * @param formName
   * @param dialogConfig
   */
  public openDialog(formName: ComponentType<unknown>, configName?, data?) {
    this._dialogService.openDialog(formName, configName, data)
    .afterClosed()
    .subscribe(data => {
    });
  }

  ngOnInit(): void {
    // Subscribe to studios updates from AuthService
    this.studiosSubscription = this._authService.studiosUpdated.subscribe(studios => {
      if (studios && studios.length > 0) {
        // Sort studios alphabetically by key (studio name)
        this.studios = studios.sort((a, b) => a.key.localeCompare(b.key));
        this.filteredStudios = [...this.studios];
        LoggerService.log('Menu component received updated studios:', this.studios);
      }
    });

    // Load the studio list if user is authenticated and we don't have studios yet
    this.loadUserStudios();

    // Subscribe to router events to update the active menu item
    this._router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        // Any additional logic needed when navigation completes
      });
  }

  ngOnDestroy(): void {
    // Clean up subscription to prevent memory leaks
    if (this.studiosSubscription) {
      this.studiosSubscription.unsubscribe();
    }
  }

  /**
   * Load studios associated with the current user
   */
  loadUserStudios(): void {
    if (this._authService.is_user_authenticated() &&
        (!this._authService.studios || this._authService.studios.length === 0)) {
      LoggerService.log('Menu component loading user studios');
      // Get the studio list from the user's profile
      this._drupalRESTService.httpGET('/api_rest/v1/loadStudioList').subscribe({
        next: (data: any) => {
          if (data && Array.isArray(data) && data.length > 0) {
            this._authService.studios = data;
          }
        },
        error: (error) => {
          LoggerService.error('Error loading studios in menu component:', error);
        }
      });
    }
  }

  onStudioSearch(): void {
    if (!this.studioSearchText?.trim()) {
      this.filteredStudios = [...this.studios];
    } else {
      const searchText = this.studioSearchText.toLowerCase().trim();
      this.filteredStudios = this.studios.filter(studio =>
        studio.key.toLowerCase().includes(searchText)
      );
    }
    // Ensure filtered results remain sorted alphabetically
    this.filteredStudios.sort((a, b) => a.key.localeCompare(b.key));
  }

  trackByStudio(index: number, studio: any): any {
    return studio.id || index;
  }

  openAdminLink(link) {
    let endpoint = environment.hostUrl;
    window.open(endpoint + link, '_newtab');
  }

  openEntityComponent(component: ComponentType<unknown>, eckType: any, bundle: any, action: any, EntityID?: any, fieldsData?: {}) {
    // LoggerService.log('fieldsData')
    // LoggerService.log(fieldsData)
    this._dialogService.openDialog(component, "defaultWithData", {
      data: {
        EntityID: EntityID,
        eckType: eckType,
        bundle: bundle,
        action: action,
        fieldsData: fieldsData ?? '',
      },
    }).afterClosed().subscribe(result => {
      // Refresh calendar if available
      if (this?.refreshCalendar) {
        this.refreshCalendar();
      }
    });
  }

  private loadMenu() {
    // this._menuREST.loadedMenuItems
  }

  // Switch studios functionality.
  handleStudioClick(studio) {
    // Store the selected studio as the current active studio
    // but don't overwrite the full list of studios
    this._authService.selectedStudio = studio;

    this.navbarOpen = false;  // Close mobile navbar if open
    this.studioSearchText = '';  // Clear the search text
    this.filteredStudios = [...this.studios];  // Reset filtered studios to show all studios

    // Refresh Instructor Data.
    this._fieldsService.fetchAndSetInstructorData();

    // Capture the current route.
    const currentRoute = this._router.url;

    // Navigate to a dummy route and then back to the current route to force reload.
    this._router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this._router.navigate([currentRoute]);
    });

    // TODO: Note: No need to force navigation reload anymore
    // Components should subscribe to authService.selectedStudioChanged to react to studio changes
    // LoggerService.log('Studio switched to:', studio.key || studio.name);
  }

  get currentStudioID(): string {
    return this._authService.selectedStudio?.id;
  }

  get currentStudio(): string {
    return this._authService.selectedStudio?.name || this._authService.selectedStudio?.key;
  }

  toggleNavbar() {
    if (!this._authService.is_user_authenticated()) {
      return;
    }

    this.navbarOpen = !this.navbarOpen;
  }

  closeNavbar() {
    this.navbarOpen = false;
  }

  openCommandPalette(): void {
    const commandPaletteService = (window as any).commandPaletteService;
    if (commandPaletteService) {
      commandPaletteService.open();
    }
  }

}
